import { useTheme } from "@mui/material";
import React, { createContext, use, useMemo } from "react";
import {
  MixBasketSummary,
  MixMaterialBasketSummary,
  MaterialRead,
} from "src/store/api/generatedApi";
import { HoveredMaterialProvider } from "contexts/HoveredMaterialContext";

const processMaterialTotals = (
  materialBasketsSummary: MixMaterialBasketSummary[]
) => {
  const materialTotals = materialBasketsSummary.reduce<Record<string, number>>(
    (acc, item) => {
      const materialName = item.material;
      const mass = item.recipe_mass;
      acc[materialName] = (acc[materialName] ?? 0) + mass;
      return acc;
    },
    {}
  );

  return Object.entries(materialTotals)
    .map(([name, totalMass]) => ({ name, totalMass }))
    .sort((a, b) => b.totalMass - a.totalMass);
};

interface SearchMixDetailsContextType {
  mixBasketsSummary: MixBasketSummary[];
  mixMaterialBasketsSummary: MixMaterialBasketSummary[];
  sortedMaterials: { name: string; totalMass: number; color: string }[];
}

const SearchMixDetailsContext = createContext<
  SearchMixDetailsContextType | undefined
>(undefined);

interface SearchMixDetailsProviderProps {
  children: React.ReactNode;
  mixBasketsSummary: MixBasketSummary[];
  mixMaterialBasketsSummary: MixMaterialBasketSummary[];
  materials: MaterialRead[];
}

export const SearchMixDetailsProvider: React.FC<
  SearchMixDetailsProviderProps
> = ({ children, mixBasketsSummary, mixMaterialBasketsSummary, materials }) => {
  const theme = useTheme();
  const sortedMaterials = useMemo(
    () =>
      processMaterialTotals(mixMaterialBasketsSummary).map((material) => ({
        ...material,
        color:
          materials.find((m) => m.name === material.name)?.colour ??
          theme.palette.text.primary,
      })),
    [mixMaterialBasketsSummary, materials, theme.palette.text.primary]
  );

  const value = useMemo(
    () => ({
      mixBasketsSummary,
      mixMaterialBasketsSummary,
      sortedMaterials,
    }),
    [mixBasketsSummary, mixMaterialBasketsSummary, sortedMaterials]
  );

  return (
    <HoveredMaterialProvider>
      <SearchMixDetailsContext value={value}>
        {children}
      </SearchMixDetailsContext>
    </HoveredMaterialProvider>
  );
};

export const useSearchMixDetailsContext = () => {
  const context = use(SearchMixDetailsContext);
  if (!context) {
    throw new Error(
      "useSearchMixDetailsContext must be used within a SearchMixDetailsProvider"
    );
  }
  return context;
};
